/* eslint-disable no-nested-ternary */
import { curry, getFirstItem } from 'lib/util'
import infinite from 'app/_shared/events/search/infinite'
import isCandidate from 'app/admin/event/search/isCandidate'

/**
 * Infinite Candidate Event Handler
 *
 * If {@link searchField} has a value, it will execute {@link infinite}
 * with that search term.
 *
 * If it doesn't, it will execute {@link infinite} with the given `query`.
 * It will then map over the result, extract the organisations from the contained
 * service items and segment, sort and remove duplicate from the data.
 *
 * @param {Gaia.AppModule.Spec} module    the current module composition object
 * @param {Gaia.Component.Spec} component the current action's main component
 * @param {PlatformEvent} event           the event object that triggered this handler
 * @returns {Promise<*|(function(...[*]): (*))|{data: *}|*[]>}
 */
const infiniteCandidates = async (module, component, event) => {
  try {
    const result = await infinite('verbose', module, component, {
      ...event, detail: { ...event.detail, query: isCandidate(module, component, event) },
    })

    const aggregatedData = result.data.reduce((acc, key) => {
      // `key` is either a SI or ORG
      // - if ORG, set address doctype
      // - if SI, get installedAt ref and set machine docType
      const installedAt = getFirstItem(key.refs?.installedAt) || null
      const targetOrganisation = installedAt
        ? { ...installedAt, value: { ...installedAt.value, docType: 'machine' } }
        : { ...key, value: { ...key.value, docType: 'address' } }

      const alreadyAdded = targetOrganisation
        ? acc.find(k => k.key === targetOrganisation?.key)
        : false

      // If already added
      // - Is the item we're currently adding a SI (meaning the previously added item is an ORG)
      //   -> replace previous
      // - Is the item we're currently adding an ORG (meaning the previously added item was a SI)
      //   -> don't add
      return alreadyAdded
        ? targetOrganisation.value.docType === 'machine'
          ? [...acc.filter(k => k.key !== targetOrganisation.key), targetOrganisation]
          : acc
        : [...acc, targetOrganisation]
    }, [])
    // sorting
      .sort((a, b) => (a.value.docType === 'machine' && b.value.docType === 'address'
        ? -1
        : 1))

    return {
      page: result.page,
      data: aggregatedData,
    }
  } catch (e) {
    console.error(e)
  }

  return []
}

export default curry(infiniteCandidates)
